import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function Fragtkontoret() {
  return (
    <>
      <SEO
        title="Fragtkontoret | Fastlane ApS"
        description="International Spedition. Fra vores logistiklager i Esbjerg arrangerer vi fragt af alle almindelige typer gods i hele verden. Outsourcing af fragtopgaver baseret på 55 årsværk + overtid."
        canonicalUrl="https://fastlane.dk/fragtkontoret"
      />

      <PageHeader
        title="Fragtkontoret"
        subtitle="Professionel rådgivning, international spedition og outsourcing af alle fragtopgaver."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Fragtkontoret' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* International Spedition */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <h2 className="text-2xl font-bold text-[#0f2b48]">International Spedition</h2>
            <p>
              Fra vores logistiklager i Esbjerg arrangerer vi fragt af alle almindelige typer gods i hele verden. Vi anvender så vidt muligt standard transportudstyr og standard transportformer.
            </p>
            <p>
              Ved at outsource din fragtopgaver til os får du umiddelbart mulighed for sparring med viden inden for international spedition opbygget over 55 årsværk + overtid. Eksempler på fragtopgaver vi har løst -{' '}
              <Link to="/godstransport-referencer" className="text-sky-700 font-semibold underline">
                Inspiration
              </Link>
              .
            </p>
            <p>
              Vi er praktikere med en bred viden om international fragt med bil - tog - skib og fly eller som ofte en kombination af disse fragtformer -{' '}
              <Link to="/fragt-kombineret-transport" className="text-sky-700 font-semibold underline">
                Multimodal transport
              </Link>
              .
            </p>
          </div>

          {/* Erfaringsliste */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-3">
            <h3 className="text-base font-bold text-[#0f2b48] uppercase tracking-wider">
              Vi har stor erfaring med relaterede områder som er en nødvendig basis for at kunne begå sig markedet for international spedition:
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700 pt-2">
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Fortoldningsprocedure</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Håndtering, emballering og sikring af gods</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Forsikring af gods under transport</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>CMR Loven / NSAB</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Leveringsbetingelser i.h.t. Incoterms</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Track and Trace</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Fragtbooking og opfølgning i online webportaler</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Reklamationsbehandling</span>
              </li>
            </ul>
          </div>

          {/* Først og fremmest */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <p className="font-semibold text-slate-900 text-lg">
              Først og fremmest skal en fragt gennemføres, så de afstemte forventninger indfries.
            </p>
            <p>
              Den erfaring vi har opbygget bruger vi til at sammensætte den fragtløsning som bedst passer dit transportbehov. Oftest starter vi med en afklaring og forventningsafstemning med dig/din organisation for at præcisere transportbehovet, hvorefter vi med udgangspunkt i vores erfaring stykker den bedste løsning sammen.
            </p>
            <p>
              Vi kan løse en enkelt opgave eller vi kan gennemgå dine transportbehov fra ende til anden. Vi kommer med løsninger og forslag til optimering af, hvordan godset fragtes fremadrettet.
            </p>
            <p>
              Vi sikrer god kvalitet og kommunikation, så du trygt kan outsource dine fragtopgaver via Fastlane.
            </p>

            <div className="pt-2">
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-16-rev-00-hvordan-jeg-f-r-den-rigtige-fragtpris.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs font-semibold text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-200 px-3.5 py-2 rounded-lg transition-colors gap-2"
              >
                <i className="fa-solid fa-file-pdf text-red-600 text-sm" aria-hidden="true" />
                <span>Hvordan får jeg den rigtige fragtpris? (PDF)</span>
              </a>
            </div>
          </div>

          {/* En leverandør til alle typer */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">En leverandør til alle typer gods transport!</h2>
            <p>
              I langt de fleste tilfælde kan vi finde gode løsninger til ethvert transportbehov. Som kunde slipper du for at pleje flere leverandører. Hos os er det de samme mennesker du taler med om uanset om der er tale eksport, import, ekspres, overseas, emballering, UK, Norge, ADR gods o.s.v. Vi er et videncenter og kan derfor finde frem en præcis og god løsning på dit transportbehov uden, at du skal bruge lang tid på at ringe rundt og blive viderestillet.
            </p>
          </div>

          {/* Track and Trace */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Track and Trace</h2>
            <p>
              Vi har ikke et online system til track and trace. Os bekendt er der p.t. ikke nogen der har et system hvor man kan spore alle forsendelser. De systemer der kommer tættest på er de store kurérfirmaers fragtsystemer. Vi har valgt, at vi proaktivt følger op på de sendinger vi har sagt ja til at arrangere. Vi lægger først en opgave væk når vi har fået bekræftet, at sendingen er leveret. Ved afvigelser fra det forventede informere vi transportkøber.
            </p>
          </div>

          {/* Total outsourcing */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Total outsourcing af fragt - send en e-mail så klare vi resten!</h2>
            <p>
              Hos de fleste fragtudbydere skal du idag bestille fragten online via en portal. Hos os bestiller du blot med en mail indeholdende de rigtige informationer så sørger vi for resten.
            </p>
            <p>
              Vi synes, at det er ret krævende at holde viden omkring de enkelte portaler opdateret og anvende dem i praksis. Den personlige betjening forsvinder og dermed den naturlige afklaring, der er afgørende for om en fragtopgave bliver korrekt udført.
            </p>
            <p>
              Ingen online systemer er bedre end de informationer som tilflyder dem. Os bekendt er der ikke nogen som er 100 % opdateret med status over, hvor langt godset er kommet i processen f.eks. hvornår det forventes at levere. Det kan være lidt af et detektivarbejde at finde ud af det.
            </p>
            <p>
              Vi afklarer fragtopgaven med vores kunder, bestiller fragten (evt. online) via vore godkendte leverandører og følger den til dørs, så vi er sikre på godset er leveret.
            </p>
          </div>

          {/* Reklamationsbehandling */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Reklamationsbehandling</h2>
            <p>
              Går det en enkelt gang galt har vi en implementeret reklamationsprocedure og går proaktivt ind i løsningen på kundens side. Målet er at få løst den enkelte reklamation, finde ud af hvad der gik galt og om det kan forebygges fremadrettet.
            </p>
            <p className="font-semibold text-slate-900 text-lg">
              Hos os køber du ikke bare en fragt fra A til B, men hele pakken.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
